import React, { useCallback, useMemo, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import DynamicList from '../shared/dynamic_list'
import AddAppsModal, { AppSearchResult, privacyAppIdentity } from '../shared/add_apps_modal'

const FIELD_PREFIX = 'app_privacy_permission[app_privacy_entries_attributes]'

type AllowPermissionOption = 'None' | 'Allow'
type LocationOption = 'None' | 'WhileUsing' | 'Always'
type LocationAccuracyOption = 'None' | 'Approximate' | 'Precise'

type AppPrivacyField =
  | 'icon'
  | 'name'
  | 'bundleIdentifier'
  | 'organizationJustification'
  | 'accessibility'
  | 'bluetooth'
  | 'camera'
  | 'dictation'
  | 'localNetwork'
  | 'location'
  | 'locationAccuracy'
  | 'microphone'

type FieldErrors = Partial<Record<AppPrivacyField, string[]>>

interface AppPrivacyEntryItem {
  iconUrl?: string
  name: string
  bundleIdentifier: string
  designatedRequirement: string
  organizationJustification: string
  accessibility: AllowPermissionOption
  bluetooth: AllowPermissionOption
  camera: AllowPermissionOption
  dictation: AllowPermissionOption
  localNetwork: AllowPermissionOption
  location: LocationOption
  locationAccuracy: LocationAccuracyOption
  microphone: AllowPermissionOption
  errors?: FieldErrors
  id?: number | string
}

type AppPrivacyEntryRow = Omit<AppPrivacyEntryItem, 'errors'>

function displayName(row: AppPrivacyEntryRow) {
  return row.name || row.bundleIdentifier
}

function BundleIdentifierValue({
  bundleIdentifier,
  designatedRequirement,
  rowNum,
}: {
  bundleIdentifier: string
  designatedRequirement: string
  rowNum: number
}) {
  const requirement = designatedRequirement.trim()

  return (
    <>
      <span>{bundleIdentifier}</span>
      {requirement ? (
        <>
          &nbsp;
          <OverlayTrigger
            placement="right"
            trigger={['hover', 'focus']}
            overlay={(
              <Tooltip id={`bundle-identifier-designated-requirement-${rowNum}`}>
                {`Designated Requirement: ${requirement}`}
              </Tooltip>
            )}
          >
            <span className="glyphicon glyphicon-info-sign" tabIndex={0} />
          </OverlayTrigger>
        </>
      ) : null}
    </>
  )
}

interface Policy {
  update?: boolean
}

interface AppPrivacyPermissionsTooltips {
  name?: string
  bundleIdentifier?: string
  designatedRequirement?: string
  organizationJustification?: string
  accessibility?: string
  bluetooth?: string
  camera?: string
  dictation?: string
  localNetwork?: string
  location?: string
  locationAccuracy?: string
  microphone?: string
}

interface AppPrivacyPermissionsProps {
  data: {
    appPrivacyEntries?: AppPrivacyEntryItem[]
  }
  policy: Policy
  meta: {
    allowPermissionOptions: string[]
    locationOptions: string[]
    locationAccuracyOptions: string[]
    countryCodes?: [string, string][]
    appleStoreCountryCode?: string
    macosSupported?: boolean
  }
  tooltips?: AppPrivacyPermissionsTooltips
}

interface DynamicListHeader {
  displayName: string
  key: AppPrivacyField
  tooltip?: string
  tooltipPlacement?: string
  multiline?: boolean
  readOnly?: boolean
  options?: { displayName: string; value: string }[]
}

const EMPTY_ENTRY: AppPrivacyEntryRow = {
  name: '',
  bundleIdentifier: '',
  designatedRequirement: '',
  organizationJustification: '',
  accessibility: 'None',
  bluetooth: 'None',
  camera: 'None',
  dictation: 'None',
  localNetwork: 'None',
  location: 'None',
  locationAccuracy: 'None',
  microphone: 'None',
}

function normalizeEntries(entries?: AppPrivacyEntryItem[]): AppPrivacyEntryItem[] {
  return (entries || []).map((entry) => ({
    iconUrl: entry.iconUrl,
    name: entry.name || '',
    bundleIdentifier: entry.bundleIdentifier || '',
    designatedRequirement: entry.designatedRequirement || '',
    organizationJustification: entry.organizationJustification || '',
    accessibility: (entry.accessibility || 'None') as AllowPermissionOption,
    bluetooth: (entry.bluetooth || 'None') as AllowPermissionOption,
    camera: (entry.camera || 'None') as AllowPermissionOption,
    dictation: (entry.dictation || 'None') as AllowPermissionOption,
    localNetwork: (entry.localNetwork || 'None') as AllowPermissionOption,
    location: (entry.location || 'None') as LocationOption,
    locationAccuracy: (entry.locationAccuracy || 'None') as LocationAccuracyOption,
    microphone: (entry.microphone || 'None') as AllowPermissionOption,
    ...(entry.errors && { errors: entry.errors }),
    ...(entry.id != null && { id: entry.id }),
  }))
}

function stripRowErrors(items: AppPrivacyEntryItem[]): AppPrivacyEntryRow[] {
  return items.map(({ errors: _errors, ...row }) => row)
}

function wrapInputWithErrors(input: React.ReactNode, messages?: string[]) {
  if (!Array.isArray(messages) || messages.length === 0) {
    return input
  }

  return (
    <div className="has-error">
      {input}
      <span className="help-block">{messages.join('. ')}</span>
    </div>
  )
}

function AppPrivacyPermissions(props: AppPrivacyPermissionsProps) {
  const {
    data,
    policy,
    meta: {
      allowPermissionOptions,
      locationOptions,
      locationAccuracyOptions,
      countryCodes = [],
      appleStoreCountryCode = 'US',
      macosSupported = true,
    },
    tooltips = {},
  } = props
  const [items, setItems] = useState<AppPrivacyEntryItem[]>(
    () => normalizeEntries(data.appPrivacyEntries)
  )
  const [modalOpen, setModalOpen] = useState(false)

  const existingBundleIds = useMemo(
    () => items.map((item) => item.bundleIdentifier).filter(Boolean),
    [items]
  )

  const existingAppIdentities = useMemo(
    () => items
      .filter((item) => item.bundleIdentifier)
      .map((item) => privacyAppIdentity(item.bundleIdentifier, item.designatedRequirement)),
    [items]
  )

  const onAddApp = useCallback((searchResult: AppSearchResult, designatedRequirement = '') => {
    setItems((previousItems) => [
      ...previousItems,
      {
        ...EMPTY_ENTRY,
        name: searchResult.name,
        bundleIdentifier: searchResult.bundleIdentifier,
        designatedRequirement,
        iconUrl: searchResult.iconUrl,
      },
    ])
    setModalOpen(false)
  }, [])

  const onRemoveItem = useCallback((_removedItem: AppPrivacyEntryRow, removedIndex: number) => {
    setItems((previousItems) => previousItems.filter((_item, index) => index !== removedIndex))
  }, [])

  const onChangeItem = useCallback((updatedItem: AppPrivacyEntryRow, updatedIndex: number) => {
    setItems((previousItems) => previousItems.map((item, index) => (
      index === updatedIndex ? { ...item, ...updatedItem } : item
    )))
  }, [])

  const allowOptions = useMemo(
    () => allowPermissionOptions.map((option) => ({ displayName: option, value: option })),
    [allowPermissionOptions]
  )

  const locationSelectOptions = useMemo(
    () => locationOptions.map((option) => ({ displayName: option, value: option })),
    [locationOptions]
  )

  const locationAccuracySelectOptions = useMemo(
    () => locationAccuracyOptions.map((option) => ({ displayName: option, value: option })),
    [locationAccuracyOptions]
  )

  const headers = useMemo((): DynamicListHeader[] => [
    {
      displayName: '',
      key: 'icon',
      readOnly: true,
    },
    {
      displayName: 'Name',
      key: 'name',
      readOnly: true,
      tooltip: tooltips.name,
      tooltipPlacement: 'right',
    },
    {
      displayName: 'Bundle Identifier',
      key: 'bundleIdentifier',
      readOnly: true,
      tooltip: tooltips.bundleIdentifier,
      tooltipPlacement: 'right',
    },
    {
      displayName: 'Organization Justification',
      key: 'organizationJustification',
      multiline: true,
      tooltip: tooltips.organizationJustification,
    },
    ...(macosSupported ? [{
      displayName: 'Accessibility (macOS)',
      key: 'accessibility' as const,
      options: allowOptions,
      tooltip: tooltips.accessibility,
    }] : []),
    {
      displayName: 'Bluetooth',
      key: 'bluetooth',
      options: allowOptions,
      tooltip: tooltips.bluetooth,
    },
    {
      displayName: 'Camera',
      key: 'camera',
      options: allowOptions,
      tooltip: tooltips.camera,
    },
    {
      displayName: 'Dictation',
      key: 'dictation',
      options: allowOptions,
      tooltip: tooltips.dictation,
    },
    {
      displayName: 'Local Network',
      key: 'localNetwork',
      options: allowOptions,
      tooltip: tooltips.localNetwork,
    },
    {
      displayName: 'Location',
      key: 'location',
      options: locationSelectOptions,
      tooltip: tooltips.location,
    },
    {
      displayName: 'Location Accuracy',
      key: 'locationAccuracy',
      options: locationAccuracySelectOptions,
      tooltip: tooltips.locationAccuracy,
    },
    {
      displayName: 'Microphone',
      key: 'microphone',
      options: allowOptions,
      tooltip: tooltips.microphone,
    },
  ], [
    allowOptions,
    locationSelectOptions,
    locationAccuracySelectOptions,
    macosSupported,
    tooltips,
  ])

  const renderInput = useCallback((
    input: React.ReactNode,
    header: DynamicListHeader,
    row: AppPrivacyEntryRow,
    rowNum: number,
    _onChangeItem?: (event: React.ChangeEvent<HTMLInputElement>) => void,
    columnName?: string
  ) => {
    if (header.key === 'icon') {
      const iconUrl = items[rowNum]?.iconUrl

      if (!iconUrl) {
        return null
      }

      return <img src={iconUrl} className="icon" alt="" />
    }

    if (header.readOnly) {
      const fieldValue = header.key === 'name' ? row.name : row.bundleIdentifier
      const value = header.key === 'bundleIdentifier'
        ? (
          <BundleIdentifierValue
            bundleIdentifier={fieldValue}
            designatedRequirement={row.designatedRequirement}
            rowNum={rowNum}
          />
        )
        : <span>{displayName(row)}</span>

      return wrapInputWithErrors(
        <div>
          {value}
          <input type="hidden" name={columnName} value={fieldValue} />
          {header.key === 'bundleIdentifier' ? (
            <input
              type="hidden"
              name={`${FIELD_PREFIX}[${rowNum}][designated_requirement]`}
              value={row.designatedRequirement}
            />
          ) : null}
        </div>,
        items[rowNum]?.errors?.[header.key]
      )
    }

    return wrapInputWithErrors(input, items[rowNum]?.errors?.[header.key])
  }, [items])

  const openModal = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    setModalOpen(true)
  }, [])

  return (
    <div className="app-privacy-permissions dynamic-list">
      {items.length === 0 ? (
        <input type="hidden" name={`${FIELD_PREFIX}[]`} value="" />
      ) : null}

      <h3 className="app-list-header">App List</h3>
      {policy.update ? (
        <div className="buttons app-list-buttons">
          <button type="button" className="btn btn-default" onClick={openModal}>
            Add Apps
          </button>
        </div>
      ) : null}

      <DynamicList
        rows={stripRowErrors(items)}
        onRemoveItem={onRemoveItem}
        onAddItem={() => {}}
        onChangeItem={onChangeItem}
        headers={headers}
        policy={policy}
        fieldName={FIELD_PREFIX}
        renderInput={renderInput}
      />

      {!macosSupported && items.map((item, index) => (
        <input
          key={`accessibility-${index}`}
          type="hidden"
          name={`${FIELD_PREFIX}[${index}][accessibility]`}
          value={item.accessibility}
        />
      ))}

      <AddAppsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAddApp={onAddApp}
        existingBundleIds={existingBundleIds}
        queryType="app_privacy_permission"
        countryCodes={countryCodes}
        appleStoreCountryCode={appleStoreCountryCode}
        titleId="app-privacy-add-apps-modal"
        showDesignatedRequirement
        designatedRequirementHelpText={tooltips.designatedRequirement}
        existingAppIdentities={existingAppIdentities}
      />
    </div>
  )
}

export default AppPrivacyPermissions
